import React, { useState } from 'react';
import { TEAM_TIERS, WHITE_LABEL, type BillingInterval, type TeamTierKey } from '../../config/plans';

type Props = {
  /** Called when a team tier's CTA is clicked, with that tier's Price ID + interval. */
  onSelect?: (priceId: string, interval: BillingInterval, tier: TeamTierKey) => void;
  /** Disable CTAs and show a working state (e.g. while redirecting to Stripe). */
  busy?: boolean;
  /** Which tier is currently active (renders "Current plan" instead of a CTA). */
  activeTier?: TeamTierKey | null;
  /** When true, every tier CTA becomes a "Talk to us" mailto instead of Stripe checkout. */
  contactOnly?: boolean;
};

/** Mailto for the white-label / enterprise "contact sales" CTA. */
const WHITE_LABEL_MAILTO =
  'mailto:contact@blackjacktrainer.pro?subject=White-label%20%2F%20Enterprise%20inquiry';

/**
 * B2B pricing grid: Team / Group / Academy seat-band tiers plus a contact-sales
 * white-label card. Used on the landing page and in the admin team-billing panel.
 */
export const TeamPricing: React.FC<Props> = ({ onSelect, busy, activeTier, contactOnly }) => {
  const [interval, setInterval] = useState<BillingInterval>('year');

  const tierMailto = (label: string) =>
    `mailto:contact@blackjacktrainer.pro?subject=${encodeURIComponent(`${label} plan inquiry`)}`;

  return (
    <div>
      <div className="flex justify-center mb-6">
        <div className="inline-flex bg-gray-800 rounded-full p-1 border border-gray-700">
          <button
            type="button"
            onClick={() => setInterval('month')}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
              interval === 'month' ? 'bg-emerald-600 text-white' : 'text-gray-300 hover:text-white'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setInterval('year')}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${
              interval === 'year' ? 'bg-emerald-600 text-white' : 'text-gray-300 hover:text-white'
            }`}
          >
            Annual
            <span className="text-[10px] font-bold bg-amber-400 text-gray-900 px-1.5 py-0.5 rounded-full">
              2 MONTHS FREE
            </span>
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TEAM_TIERS.map((tier) => {
          const price = interval === 'month' ? tier.monthly : tier.annual;
          const isActive = activeTier === tier.key;
          return (
            <div
              key={tier.key}
              className={`rounded-2xl border p-5 flex flex-col ${
                isActive ? 'border-emerald-500 bg-emerald-500/5' : 'border-gray-700 bg-gray-900'
              }`}
            >
              <div className="text-sm font-bold uppercase tracking-wide text-emerald-400">{tier.label}</div>
              <div className="mt-2 flex items-end gap-1">
                <span className="text-3xl font-extrabold text-white">{price.label}</span>
                <span className="text-gray-400 mb-1 text-sm">{interval === 'year' ? '/yr' : '/mo'}</span>
              </div>
              <div className="text-sm text-gray-300 mt-1">Up to {tier.seatLimit} players</div>
              <div className="text-xs text-gray-500 mt-1 flex-grow">{tier.blurb}</div>
              {isActive ? (
                <div className="mt-4 text-center text-sm font-semibold text-emerald-400 py-2.5">
                  Current plan
                </div>
              ) : contactOnly ? (
                <a
                  href={tierMailto(tier.label)}
                  className="mt-4 w-full inline-block text-center px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold text-sm transition-colors"
                >
                  Talk to us
                </a>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onSelect?.(price.priceId, interval, tier.key)}
                  className="mt-4 w-full px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {busy ? 'Redirecting…' : 'Choose'}
                </button>
              )}
            </div>
          );
        })}

        {/* White-label / enterprise — contact sales */}
        <div className="rounded-2xl border border-amber-500/40 bg-gradient-to-b from-amber-500/10 to-transparent p-5 flex flex-col">
          <div className="text-sm font-bold uppercase tracking-wide text-amber-300">{WHITE_LABEL.label}</div>
          <div className="mt-2 text-2xl font-extrabold text-white">{WHITE_LABEL.priceFrom}</div>
          <div className="text-xs text-gray-400 mt-2 flex-grow">{WHITE_LABEL.blurb}</div>
          <a
            href={WHITE_LABEL_MAILTO}
            className="mt-4 w-full inline-block text-center px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold text-sm transition-colors"
          >
            Contact sales
          </a>
        </div>
      </div>
    </div>
  );
};
